import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Brain, Castle, ListOrdered, Shield, User } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { Loader } from '@/components/common/Loader';
import { useGetCocLinkQuery } from '@/services/qclansApi';
import { cn } from '@/lib/cn';

/**
 * QClans (Clash of Clans) home — entry point for the `/coc` surface.
 *
 * Shows a link-status row (how many CoC accounts are linked, link CTA when
 * none) plus quick-nav cards to the sub-screens: My profile / Clan /
 * Competitions / AI Coach. Each sub-screen handles its own "link first" state.
 */
export function CocHome() {
  const { t } = useTranslation('coc');

  const linkQuery = useGetCocLinkQuery(undefined, { refetchOnMountOrArgChange: true });
  const linkedCount = linkQuery.data?.accounts.length ?? 0;
  const linked = linkedCount > 0;

  return (
    <div className="flex flex-col gap-5 px-4 pb-6 pt-5">
      <header className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/15 text-brand">
            <Shield aria-hidden className="h-5 w-5" />
          </span>
          <h1 className="text-2xl font-bold text-fg">{t('home.title')}</h1>
        </div>
        <p className="text-sm text-fg-muted">{t('home.subtitle')}</p>
      </header>

      {linkQuery.isLoading ? (
        <div className="flex h-20 items-center justify-center">
          <Loader />
        </div>
      ) : linked ? (
        <Link
          to="/coc/link"
          className="flex items-center gap-3 rounded-xl bg-bg-secondary p-4 transition-opacity active:opacity-80"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-success/15 text-success">
            <User aria-hidden className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-fg">
              {t('home.linkedCount', { count: linkedCount })}
            </p>
            <p className="text-[11px] text-fg-muted">{linkQuery.data?.accounts[0]?.playerName}</p>
          </div>
        </Link>
      ) : (
        <section className="flex flex-col gap-3 rounded-xl bg-bg-secondary p-4">
          <p className="text-sm text-fg-muted">{t('home.linkPrompt')}</p>
          <Link
            to="/coc/link"
            className="flex items-center justify-center gap-2 rounded-xl bg-brand px-4 py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80"
          >
            <Shield aria-hidden className="h-4 w-4" />
            {t('home.linkCta')}
          </Link>
        </section>
      )}

      <nav className="flex flex-col gap-3">
        <NavCard
          to="/coc/profile"
          icon={<User aria-hidden className="h-5 w-5" strokeWidth={2.5} />}
          title={t('home.nav.profile')}
          subtitle={t('home.nav.profileSub')}
        />
        <NavCard
          to="/coc/clan"
          icon={<Castle aria-hidden className="h-5 w-5" strokeWidth={2.5} />}
          title={t('home.nav.clan')}
          subtitle={t('home.nav.clanSub')}
        />
        <NavCard
          to="/coc/competitions"
          icon={<ListOrdered aria-hidden className="h-5 w-5" strokeWidth={2.5} />}
          title={t('home.nav.competitions')}
          subtitle={t('home.nav.competitionsSub')}
        />
        <NavCard
          to="/coc/coach"
          icon={<Brain aria-hidden className="h-5 w-5" strokeWidth={2.5} />}
          title={t('home.nav.coach')}
          subtitle={t('home.nav.coachSub')}
        />
      </nav>

      <p className="px-1 text-[11px] leading-snug text-fg-muted">{t('disclaimer')}</p>
    </div>
  );
}

function NavCard({
  to,
  icon,
  title,
  subtitle,
}: {
  to: string;
  icon: ReactNode;
  title: string;
  subtitle: string;
}) {
  const haptics = useHaptics();
  return (
    <Link
      to={to}
      onClick={() => haptics.impact('light')}
      className={cn(
        'flex items-center gap-3 rounded-2xl border border-brand/40 bg-brand/5 px-4 py-3 text-sm',
        'transition-opacity active:opacity-80',
      )}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand/15 text-brand">
        {icon}
      </span>
      <div className="flex flex-col">
        <span className="font-bold text-fg">{title}</span>
        <span className="text-[11px] text-fg-muted">{subtitle}</span>
      </div>
    </Link>
  );
}
